import { ChevronDown, Trash2 } from "lucide-react";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import "./RoleList.css";

import Table from "../../lib/ui/components/table/Table";
import { BodyText } from "../../lib/ui/components/typography/Typography";
import Link from "../../lib/ui/components/link/Link";
import Dropdown from "../../lib/ui/components/dropdown/Dropdown";
import Tooltip from "../../lib/ui/components/tooltip/Tooltip";
import Checkbox from "../../lib/ui/components/checkbox/Checkbox";
import Button from "../../lib/ui/components/button/Button";
import SearchInput from "../../lib/ui/components/input/SearchInput";
import FiltersButton from "../../lib/ui/components/filters-button/FiltersButton";
import Toggle from "../../lib/ui/components/toggle/Toggle";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import AnalysisRubricRequiredModal from "../ConfirmationModal/AnalysisRubricRequiredModal";
import Modal from "../../lib/ui/components/Modal";
import AutomaticInterviewEnableDetails, {
  AUTO_INTERVIEW_INVITE_SCOPE,
} from "../ConfirmationModal/AutomaticInterviewEnableDetails";
import { getModalConfig } from "../ConfirmationModal/modalHelper";

// Store
import { useStore } from "../../store";

// Utils
import { calculatePageAfterDeletion } from "../../utils/tableUtils";
import { isAnalysisRubricRequiredDetail } from "../../utils/analysisRubricPreflight";
import { useTablePagination } from "../../hooks/useTablePagination";
import { rolesCompanyScope } from "../../utils/listFilterScopes";
import {
  clearRolesListFilters,
  DEFAULT_INTERVIEW_MESSAGING,
  readRolesListFilters,
  writeRolesListFilters,
} from "../../utils/rolesListFilterStorage";
import {
  DELETE_JOB,
  DUPLICATE_JOB,
  CLOSE_JOB,
  OPEN_JOB,
  UPDATE_AUTOMATIC_INTERVIEWS,
} from "../../lib/urls";

function RoleFiltersDrawerSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="w-full">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 bg-transparent py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 rounded dark:focus-visible:ring-offset-gray-800"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <BodyText weight="bold">{title}</BodyText>
        <ChevronDown
          className={`h-3 w-3 shrink-0 text-gray-600 transition-transform duration-200 dark:text-gray-400 ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open ? (
        <div className="flex flex-col gap-2 pt-2">{children}</div>
      ) : null}
    </div>
  );
}

const INTERVIEW_MESSAGING_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
  { value: "Needs Setup", label: "Needs Setup" },
];

const ROLE_PURPOSE_OPTIONS = [
  { value: "new_applicants", label: "New Applicants" },
  { value: "candidate_rediscovery", label: "Candidate Rediscovery" },
  { value: "candidate_outreach", label: "Candidate Outreach" },
];

function getInterviewMessagingStatus(job) {
  if (job.not_setup) return 'Needs Setup';
  if (job.status === 'CLOSE') return 'Inactive';
  if (job.status === 'OPEN') return 'Active';
  if (job.status === 'INCOMPLETE') return 'Needs Setup';
  return job.status;
}

const RoleList = ({ jobs = [], loading = false, onRefresh }) => {
  const navigate = useNavigate();
  const token = useStore((state) => state.token);
  const { id: companyId } = useStore((state) => state.company);

  // Bulk selection state
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [deleteCount, setDeleteCount] = useState(1);

  // Automatic interviews: confirm before enabling (per job row)
  const [autoInterviewModalJob, setAutoInterviewModalJob] = useState(null);
  const [analysisRubricWarningJob, setAnalysisRubricWarningJob] = useState(null);
  const [autoInterviewSaving, setAutoInterviewSaving] = useState(false);
  const [autoInterviewTogglingId, setAutoInterviewTogglingId] = useState(null);
  const [messagingTogglingId, setMessagingTogglingId] = useState(null);
  const [messagingStatusOverride, setMessagingStatusOverride] = useState({});
  const [autoInterviewInviteScope, setAutoInterviewInviteScope] = useState(
    AUTO_INTERVIEW_INVITE_SCOPE.NEW_ONLY
  );
  const [autoInterviewSelectedStages, setAutoInterviewSelectedStages] = useState([]);
  const [autoInterviewEnableReady, setAutoInterviewEnableReady] = useState({
    loading: false,
    error: "",
    canConfirm: true,
  });

  // Filter state
  const [interviewMessagingFilter, setInterviewMessagingFilter] = useState(
    new Set(DEFAULT_INTERVIEW_MESSAGING)
  );
  const [autoSendFilter, setAutoSendFilter] = useState("all");
  const [rolePurposeFilter, setRolePurposeFilter] = useState("all");
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);

  const listFilterScope = rolesCompanyScope(companyId);
  const savedFiltersRef = useRef(null);
  const filtersPersistReadyRef = useRef(false);
  const restoredTablePrefsRef = useRef(false);

  useEffect(() => {
    filtersPersistReadyRef.current = false;
    restoredTablePrefsRef.current = false;
    savedFiltersRef.current = readRolesListFilters(companyId);
    const saved = savedFiltersRef.current;
    if (!saved) {
      filtersPersistReadyRef.current = true;
      return;
    }
    setRoleSearchQuery(saved.roleSearchQuery);
    setInterviewMessagingFilter(new Set(saved.interviewMessagingFilter));
    setAutoSendFilter(saved.autoSendFilter);
    setRolePurposeFilter(saved.rolePurposeFilter);
    filtersPersistReadyRef.current = true;
  }, [companyId, listFilterScope]);

  // Filtering logic
  const filteredJobs = useMemo(() => {
    let filtered = jobs || [];

    if (roleSearchQuery.trim()) {
      const query = roleSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(job =>
        (job.role_name || '').toLowerCase().includes(query)
      );
    }

    filtered = filtered.filter(job => {
      const status = getInterviewMessagingStatus(job);
      return interviewMessagingFilter.has(status);
    });

    if (autoSendFilter !== "all") {
      const wantAutoSend = autoSendFilter === "on";
      filtered = filtered.filter(job => !!job.automatic_interviews === wantAutoSend);
    }

    if (rolePurposeFilter !== "all") {
      filtered = filtered.filter(job =>
        (job.role_purpose || 'new_applicants') === rolePurposeFilter
      );
    }

    return filtered;
  }, [jobs, roleSearchQuery, interviewMessagingFilter, autoSendFilter, rolePurposeFilter]);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (interviewMessagingFilter.size !== 3) count += 1;
    if (autoSendFilter !== "all") count += 1;
    if (rolePurposeFilter !== "all") count += 1;
    return count;
  }, [interviewMessagingFilter, autoSendFilter, rolePurposeFilter]);

  const clearAllFilters = useCallback(() => {
    clearRolesListFilters(companyId);
    setInterviewMessagingFilter(new Set(DEFAULT_INTERVIEW_MESSAGING));
    setAutoSendFilter("all");
    setRolePurposeFilter("all");
    setRoleSearchQuery("");
  }, [companyId]);

  // Table pagination and sorting
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    currentItems: currentJobs,
    sortedItems: sortedJobs,
    sortField,
    sortDirection,
    handlePageChange,
    handleSort,
    setCurrentPage,
    resetToFirstPage,
    setSortField,
    setSortDirection,
  } = useTablePagination(filteredJobs, {
    itemsPerPage: 10,
    defaultSortField: 'updated_at',
    defaultSortDirection: 'desc',
    sortFieldConfig: {
      created_at: {
        type: 'date',
        getValue: (item) => item.created_at
      },
      role_name: {
        type: 'string',
        getValue: (item) => item.role_name,
        fallback: (a, b) => {
          const aName = (a.role_name || '').toLowerCase();
          const bName = (b.role_name || '').toLowerCase();
          return aName.localeCompare(bName);
        }
      },
      updated_at: {
        type: 'date',
        getValue: (item) => item.updated_at
      }
    },
    defaultSorts: {
      updated_at: 'desc',
      role_name: 'asc'
    }
  });




  // Reset selection when jobs change
  useEffect(() => {
    setSelectedJobs(new Set());
  }, [jobs]);

  // Reset selection and page when filters change
  useEffect(() => {
    setSelectedJobs(new Set());
    if (!filtersPersistReadyRef.current) return;
    if (savedFiltersRef.current && !restoredTablePrefsRef.current) return;
    resetToFirstPage();
  }, [interviewMessagingFilter, autoSendFilter, rolePurposeFilter, roleSearchQuery, resetToFirstPage]);

  useEffect(() => {
    const saved = savedFiltersRef.current;
    if (
      !filtersPersistReadyRef.current ||
      restoredTablePrefsRef.current ||
      !saved
    ) {
      return;
    }
    if (saved.currentPage) {
      setCurrentPage(saved.currentPage);
    }
    if (saved.sortField) {
      setSortField(saved.sortField);
    }
    if (saved.sortDirection) {
      setSortDirection(saved.sortDirection);
    }
    restoredTablePrefsRef.current = true;
  }, [filteredJobs.length, setCurrentPage, setSortField, setSortDirection]);

  useEffect(() => {
    if (!companyId || !filtersPersistReadyRef.current) return;
    writeRolesListFilters(companyId, {
      roleSearchQuery,
      interviewMessagingFilter: Array.from(interviewMessagingFilter),
      autoSendFilter,
      rolePurposeFilter,
      currentPage,
      sortField,
      sortDirection,
    });
  }, [
    companyId,
    roleSearchQuery,
    interviewMessagingFilter,
    autoSendFilter,
    rolePurposeFilter,
    currentPage,
    sortField,
    sortDirection,
  ]);

  // Bulk selection handlers
  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      const allJobIds = new Set(filteredJobs?.map(job => job.id) || []);
      setSelectedJobs(allJobIds);
    } else {
      setSelectedJobs(new Set());
    }
  }, [filteredJobs]);

  const handleSelectJob = useCallback((jobId, checked) => {
    const newSelected = new Set(selectedJobs);
    if (checked) {
      newSelected.add(jobId);
    } else {
      newSelected.delete(jobId);
    }
    setSelectedJobs(newSelected);
  }, [selectedJobs]);

  // Get selected jobs data
  const selectedJobsData = useMemo(() => {
    return jobs.filter(job => selectedJobs.has(job.id));
  }, [jobs, selectedJobs]);

  // Determine bulk action button text and state
  const bulkActionState = useMemo(() => {
    if (selectedJobsData.length === 0) {
      return { buttonText: 'Deactivate', disabled: true };
    }

    // Check if any selected job has 'INCOMPLETE' status (Needs Questions)
    const hasIncompleteJobs = selectedJobsData.some(job => job.status === 'INCOMPLETE');

    if (hasIncompleteJobs) {
      // Any selected job needs questions - disable bulk action
      return { buttonText: 'Deactivate', disabled: true };
    }

    // Only consider jobs that are not incomplete
    const eligibleJobs = selectedJobsData.filter(job => job.status !== 'INCOMPLETE');
    const closedJobs = eligibleJobs.filter(job => job.status === 'CLOSE');
    const openJobs = eligibleJobs.filter(job => job.status === 'OPEN');

    if (closedJobs.length === eligibleJobs.length) {
      // All eligible jobs are closed
      return { buttonText: 'Activate', disabled: false };
    } else if (openJobs.length === eligibleJobs.length) {
      // All eligible jobs are open
      return { buttonText: 'Deactivate', disabled: false };
    } else {
      // Mixed selection
      return { buttonText: 'Deactivate', disabled: true };
    }
  }, [selectedJobsData]);

  const openDeleteModal = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    setJobToDelete(job);
    setIsBulkDelete(false);
    setDeleteCount(1);
    setDeleteModalOpen(true);
  };

  const openBulkDeleteModal = () => {
    setJobToDelete(null);
    setIsBulkDelete(true);
    setDeleteCount(selectedJobs.size);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setJobToDelete(null);
    setIsBulkDelete(false);
  };

  const confirmDeleteJob = async () => {
    if (isBulkDelete) {
      await bulkDeleteJobs();
      return;
    }

    if (!jobToDelete) return;

    setIsLoading(true);

    try {
      const response = await fetch(DELETE_JOB(companyId, jobToDelete.id), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete job");
      }

      // Calculate the appropriate page after deletion
      const newPage = calculatePageAfterDeletion(1, currentPage, totalPages, itemsPerPage, sortedJobs.length);
      setCurrentPage(newPage);

      if (onRefresh) {
        await onRefresh();
      }

      closeDeleteModal();
      toast.success("Job deleted successfully");
    } catch (error) {
      console.error("Error deleting job:", error);
      toast.error("Failed to delete job. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const persistAutomaticInterview = async (jobId, value, options = {}) => {
    if (!companyId || !token) {
      toast.error("Missing company or session");
      return;
    }
    setAutoInterviewTogglingId(jobId);
    try {
      const body = { automatic_interviews: value };
      if (value === true) {
        body.include_existing_candidates =
          !!options.includeExistingWithoutSession;
        if (options.interviewEligibleStageNames) {
          body.interview_eligible_stage_names =
            options.interviewEligibleStageNames;
        }
      }
      const response = await fetch(
        UPDATE_AUTOMATIC_INTERVIEWS(companyId, jobId),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (isAnalysisRubricRequiredDetail(data.detail)) {
          const job = jobs.find((item) => item.id === jobId);
          setAnalysisRubricWarningJob(job || { id: jobId, role_name: "Role" });
          return;
        }
        throw new Error(
          typeof data.detail === "string"
            ? data.detail
            : data.detail?.message || "Failed to update automatic interviews"
        );
      }
      toast.success(
        value
          ? "Automatic interviews enabled"
          : "Automatic interviews disabled"
      );
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error updating automatic interviews:", error);
      toast.error("Failed to update automatic interviews. Please try again.");
    } finally {
      setAutoInterviewTogglingId(null);
      setAutoInterviewSaving(false);
      setAutoInterviewModalJob(null);
    }
  };

  const confirmEnableAutomaticInterview = () => {
    if (!autoInterviewModalJob) return;
    setAutoInterviewSaving(true);
    void persistAutomaticInterview(autoInterviewModalJob.id, true, {
      includeExistingWithoutSession:
        autoInterviewInviteScope === AUTO_INTERVIEW_INVITE_SCOPE.INCLUDE_EXISTING,
      interviewEligibleStageNames: autoInterviewSelectedStages,
    });
  };

  const closeAutomaticInterviewModal = () => {
    if (autoInterviewSaving) return;
    setAutoInterviewModalJob(null);
  };

  const handleAutomaticInterviewToggle = (job, checked) => {
    if (!checked) {
      void persistAutomaticInterview(job.id, false);
      return;
    }
    if (!job.automatic_interviews) {
      if (!job.has_analysis_criteria) {
        setAnalysisRubricWarningJob(job);
        return;
      }
      setAutoInterviewInviteScope(AUTO_INTERVIEW_INVITE_SCOPE.NEW_ONLY);
      setAutoInterviewSelectedStages(job.interview_eligible_stage_names || []);
      setAutoInterviewEnableReady({
        loading: Boolean(job.ats_job_id),
        error: "",
        canConfirm: !job.ats_job_id,
      });
      setAutoInterviewModalJob(job);
    }
  };

  const handleDuplicateJob = async (jobId) => {
    try {
      const response = await fetch(DUPLICATE_JOB(companyId, jobId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to duplicate job");
      }

      if (onRefresh) {
        await onRefresh();
      }
      toast.success("Job duplicated successfully");
    } catch (error) {
      console.error("Error duplicating job:", error);
      toast.error("Failed to duplicate job. Please try again.");
    }
  };

  const handleCloseJob = async (jobId, showToast = true) => {
    const loadingToastId = showToast ? toast.loading("Deactivating role...") : null;
    try {
      const response = await fetch(CLOSE_JOB(companyId, jobId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to close job");
      }

      if (showToast && onRefresh) {
        await onRefresh();
      }

      if (showToast) {
        toast.dismiss(loadingToastId);
        toast.success("Role deactivated successfully");
      }
    } catch (error) {
      console.error("Error closing job:", error);
      if (showToast) {
        toast.error("Failed to deactivate role. Please try again.", { id: loadingToastId });
      }
      throw error; // Re-throw for bulk operations to handle
    }
  };

  const handleOpenJob = async (jobId, showToast = true) => {
    const loadingToastId = showToast ? toast.loading("Activating role...") : null;
    try {
      const response = await fetch(OPEN_JOB(companyId, jobId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to open job");
      }

      if (showToast && onRefresh) {
        await onRefresh();
      }

      if (showToast) {
        toast.dismiss(loadingToastId);
        toast.success("Role activated successfully");
      }
    } catch (error) {
      console.error("Error opening job:", error);
      if (showToast) {
        toast.error("Failed to activate role. Please try again.", { id: loadingToastId });
      }
      throw error;
    }
  };

  const clearMessagingStatusOverride = (jobId) => {
    setMessagingStatusOverride((prev) => {
      if (!(jobId in prev)) return prev;
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
  };

  const handleInterviewMessagingToggle = async (job, checked) => {
    setMessagingStatusOverride((prev) => ({
      ...prev,
      [job.id]: checked ? "OPEN" : "CLOSE",
    }));
    setMessagingTogglingId(job.id);
    try {
      if (checked) {
        await handleOpenJob(job.id);
      } else {
        await handleCloseJob(job.id);
      }
      clearMessagingStatusOverride(job.id);
    } catch {
      clearMessagingStatusOverride(job.id);
    } finally {
      setMessagingTogglingId(null);
    }
  };

  // Bulk action functions

  const bulkDeleteJobs = async () => {
    if (selectedJobs.size === 0) return;

    const originalCount = selectedJobs.size;
    const jobsToDelete = Array.from(selectedJobs);

    setIsLoading(true);
    try {
      const deletePromises = jobsToDelete.map(jobId =>
        fetch(DELETE_JOB(companyId, jobId), {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
      );

      const responses = await Promise.all(deletePromises);
      const failedDeletes = responses.filter(response => !response.ok);

      if (failedDeletes.length > 0) {
        throw new Error(`Failed to delete ${failedDeletes.length} job(s)`);
      }

      // Calculate the appropriate page after deletion
      const newPage = calculatePageAfterDeletion(originalCount, currentPage, totalPages, itemsPerPage, sortedJobs.length);
      setCurrentPage(newPage);

      setSelectedJobs(new Set());

      if (onRefresh) {
        await onRefresh();
      }

      closeDeleteModal();

      const message = originalCount === 1
        ? '1 job has been deleted successfully.'
        : `${originalCount} jobs have been deleted successfully.`;
      toast.success(message);
    } catch (error) {
      console.error("Error deleting jobs:", error);
      toast.error("Failed to delete jobs. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const bulkCloseOpenJobs = async () => {
    if (selectedJobsData.length === 0) return;

    const currentJobsData = jobs.filter(job => selectedJobs.has(job.id));
    // Filter out jobs with 'INCOMPLETE' status (Needs Questions)
    const eligibleJobsData = currentJobsData.filter(job => job.status !== 'INCOMPLETE');
    const closedJobs = eligibleJobsData.filter(job => job.status === 'CLOSE');
    const openJobs = eligibleJobsData.filter(job => job.status === 'OPEN');

    setIsLoading(true);
    try {
      if (closedJobs.length === eligibleJobsData.length) {
        // All selected jobs are inactive, activate them
        const loadingToastId = toast.loading("Activating roles...");
        try {
          const openPromises = closedJobs.map(job => handleOpenJob(job.id, false));
          await Promise.all(openPromises);

          toast.dismiss(loadingToastId);

          if (onRefresh) {
            await onRefresh();
          }

          const message = closedJobs.length === 1
            ? '1 role has been activated successfully'
            : `${closedJobs.length} roles have been activated successfully`;
          toast.success(message);
        } catch (error) {
          toast.dismiss(loadingToastId);
          throw error;
        }
      } else if (openJobs.length === eligibleJobsData.length) {
        // All selected jobs are active, deactivate them
        const loadingToastId = toast.loading("Deactivating roles...");
        try {
          const closePromises = openJobs.map(job => handleCloseJob(job.id, false));
          await Promise.all(closePromises);

          toast.dismiss(loadingToastId);

          if (onRefresh) {
            await onRefresh();
          }

          const message = openJobs.length === 1
            ? '1 role has been deactivated successfully'
            : `${openJobs.length} roles have been deactivated successfully`;
          toast.success(message);
        } catch (error) {
          toast.dismiss(loadingToastId);
          throw error;
        }
      }

      setSelectedJobs(new Set());
    } catch (error) {
      console.error("Error in bulk operation:", error);
      toast.error("Failed to complete bulk operation. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Show empty state if no jobs
  if (!loading && (!jobs || jobs.length === 0)) {
    return (
      <div className="text-center py-12">
        <BodyText className="text-gray-500">No jobs found</BodyText>
      </div>
    );
  }

  return (
    <div className="mt-2 mb-4 max-w-full">
      {(jobs?.length > 0 || loading) && (
        <>
          {loading ? (
            <div
              className="mb-4 mx-4 flex flex-wrap items-start justify-between gap-3 pt-2"
              aria-busy="true"
              aria-label="Loading roles"
            >
              <div className="flex flex-wrap items-center gap-4">
                <div className="h-4 w-28 animate-pulse rounded-md bg-gray-100" />
                <div className="h-4 w-36 animate-pulse rounded-md bg-gray-100" />
                <div className="h-9 w-32 animate-pulse rounded-md bg-gray-100" />
              </div>
              <div className="flex items-center gap-4">
                <div className="h-9 w-48 animate-pulse rounded-md bg-gray-100" />
                <div className="h-9 w-24 animate-pulse rounded-md bg-gray-100" />
              </div>
            </div>
          ) : (
          <>
          <div className="mb-4 mx-4 flex flex-wrap items-start justify-between gap-3 pt-2">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex shrink-0 items-center gap-2">
                <Checkbox
                  id="select-all-jobs"
                  checked={selectedJobs.size === filteredJobs?.length && filteredJobs?.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
                <BodyText
                  className="cursor-pointer"
                  weight="bold"
                  onClick={() => {
                    const checkbox = document.getElementById('select-all-jobs');
                    if (checkbox) checkbox.click();
                  }}
                >
                  {selectedJobs.size === filteredJobs?.length && filteredJobs?.length > 0 ? 'Deselect All' : 'Select All'}
                </BodyText>
              </div>
              <BodyText className="shrink-0">|</BodyText>
              <BodyText weight="bold" className="shrink-0 whitespace-nowrap">
                {selectedJobs.size} selected:
              </BodyText>
              <Button
                variant="danger"
                className="shrink-0"
                onClick={bulkCloseOpenJobs}
                disabled={selectedJobs.size === 0 || isLoading || bulkActionState.disabled}
              >
                <span className="whitespace-nowrap">{bulkActionState.buttonText}</span>
              </Button>
              <Button
                variant="danger"
                leftIcon={<Trash2 size="1em" />}
                className="shrink-0"
                onClick={openBulkDeleteModal}
                disabled={selectedJobs.size === 0 || isLoading}
              >
                <span className="whitespace-nowrap">Delete Jobs</span>
              </Button>
            </div>
            <div className="flex shrink-0 items-center gap-4">
              <SearchInput
                value={roleSearchQuery}
                onChange={(e) => setRoleSearchQuery(e.target.value)}
                onClear={() => setRoleSearchQuery("")}
                placeholder="Search"
                data-cy="roles-search-input"
              />
              <FiltersButton
                activeFilterCount={activeFilterCount}
                onClick={() => setIsFiltersDrawerOpen(true)}
                data-cy="roles-filters-button"
                countDataCy="roles-filters-count"
              />
            </div>
          </div>
          {activeFilterCount > 0 && (
            <div className="mb-2 mx-4 flex justify-end">
              <BodyText
                weight="medium"
                className="text-gray-500"
                data-cy="roles-filtered-count"
              >
                {`Showing ${filteredJobs.length} ${filteredJobs.length === 1 ? "Role" : "Roles"}`}
              </BodyText>
            </div>
          )}
          </>
          )}
        </>
      )}

      <Modal
        show={isFiltersDrawerOpen}
        onClose={() => setIsFiltersDrawerOpen(false)}
        placement="right"
        size="md"
        dismissible
      >
        <Modal.Header>Filters</Modal.Header>
        <Modal.Body className="flex flex-col gap-5">
          <RoleFiltersDrawerSection title="Active">
            <fieldset className="m-0 border-0 p-0">
              <legend className="sr-only">Active filter</legend>
              <div className="flex flex-col gap-2">
                {INTERVIEW_MESSAGING_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <Checkbox
                      id={`filter-im-${opt.value}`}
                      checked={interviewMessagingFilter.has(opt.value)}
                      onChange={(e) => {
                        const next = new Set(interviewMessagingFilter);
                        if (e.target.checked) next.add(opt.value);
                        else next.delete(opt.value);
                        setInterviewMessagingFilter(next);
                      }}
                    />
                    <BodyText weight="medium" className="cursor-pointer">
                      {opt.label}
                    </BodyText>
                  </label>
                ))}
              </div>
            </fieldset>
          </RoleFiltersDrawerSection>
          <RoleFiltersDrawerSection title="Auto-Send">
            <fieldset className="m-0 border-0 p-0">
              <legend className="sr-only">Auto-Send filter</legend>
              <div className="flex flex-col gap-2">
                {[
                  { value: "all", label: "All" },
                  { value: "on", label: "On" },
                  { value: "off", label: "Off" },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <input
                      type="radio"
                      name="autosend-filter"
                      value={opt.value}
                      checked={autoSendFilter === opt.value}
                      onChange={() => setAutoSendFilter(opt.value)}
                      className="h-4 w-4 shrink-0 border-gray-300 text-primary-700 focus:ring-primary-600 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-primary-600"
                    />
                    <BodyText weight="medium">{opt.label}</BodyText>
                  </label>
                ))}
              </div>
            </fieldset>
          </RoleFiltersDrawerSection>
          <RoleFiltersDrawerSection title="Role Purpose">
            <fieldset className="m-0 border-0 p-0">
              <legend className="sr-only">Role Purpose filter</legend>
              <div className="flex flex-col gap-2">
                {[
                  { value: "all", label: "All" },
                  ...ROLE_PURPOSE_OPTIONS,
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <input
                      type="radio"
                      name="rolepurpose-filter"
                      value={opt.value}
                      checked={rolePurposeFilter === opt.value}
                      onChange={() => setRolePurposeFilter(opt.value)}
                      className="h-4 w-4 shrink-0 border-gray-300 text-primary-700 focus:ring-primary-600 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-primary-600"
                    />
                    <BodyText weight="medium">{opt.label}</BodyText>
                  </label>
                ))}
              </div>
            </fieldset>
          </RoleFiltersDrawerSection>
          <div className="flex flex-wrap items-center justify-end gap-3 pt-1">
            <Button variant="tertiary" onClick={clearAllFilters}>
              Reset Filters
            </Button>
            <Button
              variant="primary"
              onClick={() => setIsFiltersDrawerOpen(false)}
            >
              Done
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <div className="overflow-x-auto">
      <Table
        paginated={true}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        itemsPerPage={itemsPerPage}
      >
        <Table.Head>
          <Table.HeadCell
            sortable={true}
            sortDirection={sortField === 'role_name' ? sortDirection : null}
            onSort={() => handleSort('role_name')}
          >
            Role
          </Table.HeadCell>
          <Table.HeadCell>Job ID</Table.HeadCell>
          <Table.HeadCell>Role Purpose</Table.HeadCell>
          <Table.HeadCell>Interviews Completed</Table.HeadCell>
          <Table.HeadCell
            sortable={true}
            sortDirection={sortField === 'created_at' ? sortDirection : null}
            onSort={() => handleSort('created_at')}
          >
            Created Date
          </Table.HeadCell>
          <Table.HeadCell
            sortable={true}
            sortDirection={sortField === 'updated_at' ? sortDirection : null}
            onSort={() => handleSort('updated_at')}
          >
            Last Updated
          </Table.HeadCell>
          <Table.HeadCell>Active</Table.HeadCell>
          <Table.HeadCell>Auto-send interviews</Table.HeadCell>
          <Table.HeadCell className="w-[50px]"></Table.HeadCell>
        </Table.Head>
        <Table.Body loading={loading} className="divide-y" skeletonRowCount={6}>
          {!loading && currentJobs?.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={9} className="text-center">
                {jobs.length === 0 ? 'No jobs found' : 'No jobs on this page'}
              </Table.Cell>
            </Table.Row>
          )}
          {!loading && currentJobs?.map((job) => (
            <Table.Row
              key={job.id}
              className="bg-white dark:border-gray-700 dark:bg-gray-800"
            >
              <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`select-job-${job.id}`}
                    checked={selectedJobs.has(job.id)}
                    onChange={(e) => handleSelectJob(job.id, e.target.checked)}
                  />
                  <Link
                    to={`/jobs/${encodeURIComponent(job.role_name)}/${job.id}`}
                  >
                    {job.role_name}
                  </Link>
                </div>
              </Table.Cell>
              <Table.Cell>
                <BodyText weight="normal">
                  {job.job_code || '--'}
                </BodyText>
              </Table.Cell>
              <Table.Cell>
                <BodyText weight="normal">
                  {job.role_purpose?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'New Applicants'}
                </BodyText>
              </Table.Cell>
              <Table.Cell>
                <span className="flex">
                  <BodyText weight="bold">{job.completed_interviews || 0}</BodyText><BodyText>&nbsp;of {(job.total_candidates_excluding_rejected ?? job.total_candidates) || 0}</BodyText>
                </span>
              </Table.Cell>
              <Table.Cell>
                <BodyText weight="normal">
                  {job.created_at && new Date(job.created_at).getFullYear() > 1970
                    ? new Date(job.created_at).toLocaleDateString("en-US")
                    : 'N/A'}
                </BodyText>
              </Table.Cell>
              <Table.Cell>
                <BodyText weight="normal">
                  {job.updated_at && new Date(job.updated_at).getFullYear() > 1970
                    ? new Date(job.updated_at).toLocaleDateString("en-US")
                    : 'N/A'}
                </BodyText>
              </Table.Cell>
              <Table.Cell>
                {job.not_setup || job.status === 'INCOMPLETE' ? (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      navigate(
                        `/jobs/${encodeURIComponent(job.role_name)}/${job.id}?edit=1`
                      )
                    }
                    data-cy="setup-role-button"
                  >
                    Set Up Role
                  </Button>
                ) : (
                  <Toggle
                    checked={(messagingStatusOverride[job.id] ?? job.status) === 'OPEN'}
                    disabled={
                      selectedJobs.size > 0 ||
                      messagingTogglingId === job.id
                    }
                    onChange={(checked) =>
                      handleInterviewMessagingToggle(job, checked)
                    }
                    aria-label={`${job.role_name} active`}
                    data-cy="interview-messaging-toggle"
                  />
                )}
              </Table.Cell>
              <Table.Cell>
                {job.not_setup || job.status === "INCOMPLETE" ? (
                  <Tooltip
                    content="Finish setting up this role before enabling automatic interviews"
                    position="left"
                  >
                    <span className="inline-flex opacity-50 cursor-not-allowed">
                      <Toggle
                        checked={!!job.automatic_interviews}
                        disabled={true}
                        onChange={() => { }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Toggle
                    checked={!!job.automatic_interviews}
                    disabled={
                      selectedJobs.size > 0 ||
                      autoInterviewTogglingId === job.id
                    }
                    onChange={(checked) =>
                      handleAutomaticInterviewToggle(job, checked)
                    }
                  />
                )}
              </Table.Cell>
              <Table.Cell>
                <Dropdown
                  placement="bottom-end"
                  id={`dropdown-${job.id}`}
                  disabled={selectedJobs.size > 0 || messagingTogglingId === job.id}
                  items={[
                    {
                      label: 'Duplicate',
                      onClick: () => handleDuplicateJob(job.id)
                    },
                    (messagingStatusOverride[job.id] ?? job.status) === 'OPEN' ? {
                      label: 'Deactivate',
                      onClick: () => handleCloseJob(job.id),
                      variant: 'danger',
                      disabled: job.status === 'INCOMPLETE'
                    } : {
                      label: 'Activate',
                      onClick: () => handleOpenJob(job.id),
                      variant: 'normal',
                      disabled: job.status === 'INCOMPLETE'
                    },
                    {
                      label: 'Delete',
                      onClick: () => openDeleteModal(job.id),
                      variant: 'danger'
                    }
                  ]}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteJob}
        {...getModalConfig(
          'deleteJob',
          deleteCount,
          'Job',
          jobToDelete?.role_name
        )}
        confirmButtonVariant="danger"
        isLoading={isLoading}
        loadingText={isBulkDelete ? "Deleting..." : "Deleting..."}
      />

      <AnalysisRubricRequiredModal
        isOpen={!!analysisRubricWarningJob}
        onClose={() => setAnalysisRubricWarningJob(null)}
        onConfirm={() => {
          if (!analysisRubricWarningJob) return;
          navigate(
            `/jobs/${encodeURIComponent(analysisRubricWarningJob.role_name)}/${analysisRubricWarningJob.id}?view=analysis`
          );
          setAnalysisRubricWarningJob(null);
        }}
      />

      <ConfirmationModal
        isOpen={!!autoInterviewModalJob}
        onClose={closeAutomaticInterviewModal}
        onConfirm={confirmEnableAutomaticInterview}
        {...getModalConfig("enableAutomaticInterviews")}
        details={
          <AutomaticInterviewEnableDetails
            value={autoInterviewInviteScope}
            onChange={setAutoInterviewInviteScope}
            companyId={companyId}
            jobId={autoInterviewModalJob?.id}
            token={token}
            atsJobId={autoInterviewModalJob?.ats_job_id}
            initialEligibleStages={autoInterviewModalJob?.interview_eligible_stage_names}
            selectedStages={autoInterviewSelectedStages}
            onSelectedStagesChange={setAutoInterviewSelectedStages}
            onReadyChange={setAutoInterviewEnableReady}
          />
        }
        confirmDisabled={!autoInterviewEnableReady.canConfirm}
        confirmDisabledReason={
          autoInterviewEnableReady.loading
            ? "Loading ATS stages…"
            : autoInterviewEnableReady.error ||
              (autoInterviewModalJob?.ats_job_id
                ? "Turn on at least one stage to enable automatic interviews."
                : undefined)
        }
        confirmButtonVariant="primary"
        isLoading={autoInterviewSaving}
        loadingText="Saving..."
        dismissible={!autoInterviewSaving}
      />
    </div>
  );
};

export default RoleList;
